import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TabsModule } from 'primeng/tabs';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import type { TableLazyLoadEvent } from 'primeng/types/table';
import { TooltipModule } from 'primeng/tooltip';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { catchError, of } from 'rxjs';
import { AdAdminService } from '../../../core/services/ad-admin.service';
import { AuthService } from '../../../core/services/auth.service';
import { AdminAdResponse, AdResponse, PageResponse } from '../../../core/models';
import { AppEmptyState } from '../../../shared/ui/empty-state/app-empty-state';
import { AppStatusTag, StatusTagSeverity } from '../../../shared/ui/status-tag/app-status-tag';
import { RejectAdDialogComponent } from '../reject-ad-dialog/reject-ad-dialog';
import { FcfaPipe } from '../../../shared/pipes/fcfa.pipe';

const EMPTY_PAGE: PageResponse<AdminAdResponse> = { content: [], totalElements: 0, totalPages: 0, number: 0, size: 20 };

@Component({
  selector: 'app-ad-list',
  standalone: true,
  imports: [CommonModule, TabsModule, ButtonModule, TableModule,
    TooltipModule, ToastModule,
    AppEmptyState, AppStatusTag, RejectAdDialogComponent, FcfaPipe],
  templateUrl: './ad-list.html',
  styleUrl: './ad-list.scss'
})
export class AdListComponent implements OnInit {
  private svc   = inject(AdAdminService);
  private toast = inject(MessageService);
  authService   = inject(AuthService);

  activeTab = signal('0');

  // ── Onglet "En attente" (PENDING_REVIEW) ──────────────────────────────────
  loadingPending   = signal(true);
  pagePending      = signal<PageResponse<AdminAdResponse>>(EMPTY_PAGE);
  private pageSizePending  = 20;
  private pageIndexPending = 0;

  // ── Onglet "Toutes" (sans filtre de statut) ───────────────────────────────
  loadingAll   = signal(true);
  pageAll      = signal<PageResponse<AdminAdResponse>>(EMPTY_PAGE);
  private pageSizeAll  = 20;
  private pageIndexAll = 0;

  // ── Actions en cours + miniatures résolues (partagées entre les 2 onglets) ─
  processing = signal<string | null>(null);
  imageUrls  = signal<Record<string, string>>({});

  // ── Dialogue de rejet ──────────────────────────────────────────────────────
  rejectDialogVisible = signal(false);
  rejectTarget         = signal<AdminAdResponse | null>(null);
  rejectText            = '';

  ngOnInit() {
    this.loadPending();
    this.loadAll();
  }

  // ── Chargement ───────────────────────────────────────────────────────────

  private loadPending() {
    this.loadingPending.set(true);
    this.svc.getAds(this.pageIndexPending, this.pageSizePending, 'PENDING_REVIEW')
      .pipe(catchError(() => of(null)))
      .subscribe(res => {
        if (res) {
          this.pagePending.set(res);
          this.resolveImages(res.content);
        }
        this.loadingPending.set(false);
      });
  }

  private loadAll() {
    this.loadingAll.set(true);
    this.svc.getAds(this.pageIndexAll, this.pageSizeAll)
      .pipe(catchError(() => of(null)))
      .subscribe(res => {
        if (res) {
          this.pageAll.set(res);
          this.resolveImages(res.content);
        }
        this.loadingAll.set(false);
      });
  }

  private resolveImages(ads: AdminAdResponse[]) {
    const resolved = this.imageUrls();
    ads.filter(ad => ad.imageDocumentId && !resolved[ad.id]).forEach(ad => {
      this.svc.getPhotoUrl(ad.imageDocumentId!).pipe(catchError(() => of(''))).subscribe(url => {
        if (url) this.imageUrls.update(m => ({ ...m, [ad.id]: url }));
      });
    });
  }

  onLazyPending(e: TableLazyLoadEvent) {
    const rows = e.rows ?? this.pageSizePending;
    this.pageSizePending = rows;
    this.pageIndexPending = Math.floor((e.first ?? 0) / rows);
    this.loadPending();
  }

  onLazyAll(e: TableLazyLoadEvent) {
    const rows = e.rows ?? this.pageSizeAll;
    this.pageSizeAll = rows;
    this.pageIndexAll = Math.floor((e.first ?? 0) / rows);
    this.loadAll();
  }

  onTabChange(value: string | number | undefined) {
    this.activeTab.set(String(value ?? '0'));
  }

  // ── Validation / rejet ──────────────────────────────────────────────────

  canValidate(ad: AdminAdResponse): boolean {
    return ad.status === 'PENDING_REVIEW' && ad.paymentStatus === 'PAID';
  }

  canReject(ad: AdminAdResponse): boolean {
    return ad.status === 'PENDING_REVIEW';
  }

  validate(ad: AdminAdResponse) {
    this.processing.set(ad.id);
    this.svc.validateAd(ad.id).pipe(catchError(() => of(null))).subscribe(res => {
      this.processing.set(null);
      if (res) {
        this.removeFromPending(ad.id);
        this.updateInAll(res);
        this.toast.add({ severity: 'success', summary: 'Publicité validée', detail: `« ${ad.title} » a été publiée.`, life: 4000 });
      } else {
        this.toast.add({ severity: 'error', summary: 'Erreur', detail: 'La validation a échoué.', life: 5000 });
      }
    });
  }

  startReject(ad: AdminAdResponse) {
    this.rejectTarget.set(ad);
    this.rejectText = '';
    this.rejectDialogVisible.set(true);
  }

  cancelReject() {
    this.rejectDialogVisible.set(false);
    this.rejectTarget.set(null);
  }

  confirmReject() {
    const ad = this.rejectTarget();
    if (!ad || !this.rejectText.trim()) return;
    this.processing.set(ad.id);
    this.svc.rejectAd(ad.id, this.rejectText).pipe(catchError(() => of(null))).subscribe(res => {
      if (res) {
        this.removeFromPending(ad.id);
        this.updateInAll(res);
        this.toast.add({ severity: 'warn', summary: 'Publicité rejetée', life: 3500 });
      } else {
        this.toast.add({ severity: 'error', summary: 'Erreur', detail: 'Le rejet a échoué.', life: 5000 });
      }
      this.processing.set(null);
      this.rejectDialogVisible.set(false);
      this.rejectTarget.set(null);
    });
  }

  private removeFromPending(id: string) {
    this.pagePending.update(p => ({
      ...p,
      content: p.content.filter(a => a.id !== id),
      totalElements: Math.max(0, p.totalElements - 1)
    }));
  }

  private updateInAll(res: AdResponse) {
    this.pageAll.update(p => ({
      ...p,
      content: p.content.map(a => a.id === res.id ? { ...a, ...res } : a)
    }));
  }

  // ── Formatage ────────────────────────────────────────────────────────────

  statusLabel(s: string): string {
    const map: Record<string, string> = {
      DRAFT: 'Brouillon', PENDING_REVIEW: 'En attente', PUBLISHED: 'Publiée',
      REJECTED: 'Rejetée', EXPIRED: 'Expirée'
    };
    return map[s] ?? s;
  }

  statusSeverity(s: string): StatusTagSeverity {
    const map: Record<string, StatusTagSeverity> = {
      DRAFT: 'secondary', PENDING_REVIEW: 'warn', PUBLISHED: 'success',
      REJECTED: 'danger', EXPIRED: 'contrast'
    };
    return map[s] ?? 'secondary';
  }

  paymentLabel(s: string): string {
    const map: Record<string, string> = {
      UNPAID: 'Non payé', PENDING: 'Paiement en cours', PAID: 'Payé', FAILED: 'Échec paiement'
    };
    return map[s] ?? s;
  }

  paymentSeverity(s: string): StatusTagSeverity {
    const map: Record<string, StatusTagSeverity> = {
      UNPAID: 'secondary', PENDING: 'warn', PAID: 'success', FAILED: 'danger'
    };
    return map[s] ?? 'secondary';
  }

  placementsLabel(placements: string[] = []): string {
    const map: Record<string, string> = { HOME: 'Accueil', SEARCH: 'Recherche', PROPERTY_DETAIL: 'Fiche bien' };
    return placements.length ? placements.map(p => map[p] ?? p).join(', ') : '—';
  }

  formatDate(d?: string)  { return d ? new Date(d).toLocaleDateString('fr-FR') : '—'; }
}
